<template>
    <TprmModal
        :additional-class-name="className"
        size="lg"
        allow-close
        open
        title="Grant an Exception"
        @close="$emit('close')"
    >
        <div data-test-id="ExceptionExpirationModal__content">
            <div data-test-id="ExceptionExpirationModal__description">
                Please choose when you would like the exception to expire. An exception can temporarily or permanently
                waive non-compliant criteria depending on the settings selected. Exceptions can be undone.
            </div>
            <div
                data-test-id="ExceptionExpirationModal__exceptionTypeContainer"
                class="ExceptionExpirationModal__exceptionTypeContainer"
            >
                <span class="ExceptionExpirationModal__selectionLabel">Expiration Type:</span>
                <Dropdown
                    data-test-id="ExceptionExpirationModal__exceptionTypeDropdown"
                    :options="exceptionTypeOptions"
                    :disabled-values="disabledOptions"
                    :disabled="loading"
                    @input="onTypeSelection"
                />
            </div>
            <div
                v-if="selectedType === InsuranceExceptionType.untilDate ||
                    selectedType === InsuranceExceptionType.untilCoverageExpiration"
                data-test-id="ExceptionExpirationModal__exceptionTypeBasedContent"
                class="ExceptionExpirationModal__exceptionTypeBasedContent"
            >
                <div
                    v-if="selectedType === InsuranceExceptionType.untilDate"
                    data-test-id="ExceptionExpirationModal__selectDateRow"
                    class="flex items-center"
                >
                    <div>Exception will expire on</div>
                    <DatePicker
                        data-test-id="ExceptionExpirationModal__selectDatePicker"
                        class="mx-3"
                        placeholder="Select Date"
                        :disabled="loading"
                        :min-date="tomorrowIsoString"
                        @input="onExpirationDateSelection"
                    />
                </div>
                <div v-else-if="selectedType === InsuranceExceptionType.untilCoverageExpiration" class="text-center">
                    The exception granted will expire on the requirement expiration date of
                    <strong>{{ coverageExpiration }}</strong>. The
                    exception will expire prior to the date listed above if new policy data is added.
                </div>
            </div>
            <div class="ExceptionExpirationModal__field">
                <label class="ExceptionExpirationModal__label">Note</label>
                <textarea
                    class="ExceptionExpirationModal__noteInput"
                    :value="note"
                    maxlength="250"
                    placeholder="Explain why this exception is being granted"
                    rows="4"
                    @input="$emit('update:note', $event.target.value)"
                />
                <div class="ExceptionExpirationModal__noteFooter">
                    <span class="ExceptionExpirationModal__charCount">{{ note.length }}/250</span>
                </div>
                <p class="ExceptionExpirationModal__helpText">
                    Notes should be informative and cannot be edited after saving.
                </p>
            </div>
        </div>
        <template #footer>
            <NewButton
                :loading="loading"
                data-test-id="ExceptionExpirationModal__grantButton"
                class="ExceptionExpirationModal__grantButton"
                type="primary"
                :disabled="disableGrant"
                @click="grantException"
            >
                Grant Exception
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
    @import "./index";
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { DatePicker } from '@evidentid/dashboard-commons/components/DatePicker';
    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import {
        ExceptionInput,
        ExceptionLevel,
        ExceptionType,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { EntityRequirement } from '@evidentid/tprm-portal-lib/models/entity-details/EntityRequirement.model';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    type ExceptionTypeDropdownOption = DropdownOption<ExceptionType>;

    const componentName = 'ExceptionExpirationModal';
    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'grant-exception'/
    // 'update:note' but it is neither declared in the emits option nor as an
    // 'onClose'/... prop" every time it fires.
    export default defineComponent({
        name: componentName,
        components: {
            NewButton,
            TprmModal,
            Dropdown,
            DatePicker,
        },
        props: {
            loading: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            additionalClassName: {
                type: String as PropType<string>,
                default: '',
            },
            exceptionInputs: {
                type: Array as PropType<ExceptionInput[]>,
                default: (): ExceptionInput[] => [],
            },
            requirement: {
                type: Object as PropType<EntityRequirement | null>,
                default: null,
            },
            note: {
                type: String as PropType<string>,
                default: '',
            },
        },
        emits: [ 'close', 'grant-exception', 'update:note' ],
        data() {
            return {
                selectedType: null as ExceptionType | null,
                selectedExpirationDate: null as string | null,
                exceptionTypeOptions: [
                    { value: ExceptionType.untilDate, label: 'Expire after selected calendar date' },
                    {
                        value: ExceptionType.untilCoverageExpiration,
                        label: 'Expires on expiration or updated document',
                    },
                    { value: ExceptionType.permanent, label: 'Permanent - Does not expire' },
                ] as ExceptionTypeDropdownOption[],
                InsuranceExceptionType: ExceptionType,
            };
        },
        computed: {
            disableGrant(): boolean {
                return !this.selectedType || this.isInvalidUntilDateType;
            },
            isInvalidUntilDateType(): boolean {
                return this.selectedType === ExceptionType.untilDate && this.selectedExpirationDate == null;
            },
            disabledOptions(): ExceptionType[] {
                const isInsuredLevel =
                    this.exceptionInputs.length === 1 &&
                    this.exceptionInputs[0].level === ExceptionLevel.insured;
                const todayIsoString = new Date().toISOString().split('T')[0];
                const todayDate = new Date(todayIsoString);
                const requirementExpiredOrNull = !this.requirement || Object.keys(this.requirement).length === 0
                    || !this.requirement?.policy?.expirationDate
                    || new Date(this.requirement?.policy?.expirationDate) <= todayDate;
                return isInsuredLevel || requirementExpiredOrNull
                    ? [ ExceptionType.untilCoverageExpiration ]
                    : [];
            },
            className(): string {
                const additionalClassName = this.additionalClassName ? ` ${this.additionalClassName}` : '';
                return `${componentName}${additionalClassName}`;
            },
            coverageExpiration(): string {
                return this.requirement?.policy && this.reformatDateStringToUsDate(this.requirement.policy.expirationDate) || '';
            },
            tomorrowIsoString(): string {
                const today = new Date();
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                return tomorrow.toISOString().split('T')[0];
            },
        },
        methods: {
            onTypeSelection(option: ExceptionTypeDropdownOption): void {
                this.selectedType = option.value;
                if (this.selectedType !== ExceptionType.untilDate) {
                    this.selectedExpirationDate = null;
                }
            },
            onExpirationDateSelection(date: string): void {
                this.selectedExpirationDate = date;
            },
            grantException(): void {
                if (this.disableGrant) {
                    return;
                }
                const moddedExceptions = this.exceptionInputs.map((input) => ({
                    ...input,
                    type: this.selectedType,
                    until: this.selectedExpirationDate,
                }));
                this.$emit('grant-exception', moddedExceptions);
            },
            reformatDateStringToUsDate(isoString: string): string {
                // expirationDate from requirement.policy does not include timezone so construct date as local date
                const parts = isoString.split('-');
                // part 1 -1 because month starts in 0 while iso string start 1.
                const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                return new Intl.DateTimeFormat('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                }).format(date);
            },
        },
    });
</script>
